import React, {FC, useCallback, useContext, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import * as d3 from 'd3'
import {IPeriodItem, IPeriodValues, Periods} from '../types/Periods'
import {IChartMode, ILanguage} from '../types/common'
import {getValueForDate, invRor, isEmpty, sortRows, yyyyMMdd} from './Helpers'
import YearMonthPicker from './YearMonthPicker'
import {jsPDF} from 'jspdf'
import html2canvas from 'html2canvas'
import {ChartData} from '../types/ChartData'
import InfoBox from './InfoBox'
import LegendTable from './chart/LegendTable'
import {FundChartData, Quotation} from '../types/FundChartData'
import SVGIcon from './SVGIcon'
import {
    areIntervalsOverlapping,
    differenceInDays,
    differenceInMonths,
    isBefore,
    isAfter,
    isSameDay,
    format,
} from 'date-fns'
import {TimeLocaleDefinition} from 'd3'
import {GlobalContext} from './ctx/GlobalContext'
// import * as XLSX from 'xlsx'
import * as XLSX from 'xlsx-js-style'
// import * as ExcelJS from 'exceljs'
// import {saveAs} from 'file-saver'
import {SessionContext, SessionContextType} from './ctx/SessionContext'
import AllianzLogo from './svg/AllianzLogo'
import {setFontFamily, firstColIndent, getLabelGenerated, tableDataCentering} from './XlsxTools'
import {FundProfile} from '../types/FundProfile'
import {addLinkedText, addMultiColorText, extractTextAndLinks, readCustomFonts, setCustomFont} from './PdfTools'
import axios from 'axios'
import disclaimers from './data/disclaimerData'
import {Oval, ProgressBar} from 'react-loader-spinner'
import InfoIcon from './svg/InfoIcon'

const LineChart: FC<{
    chartData: ChartData
    language: ILanguage
    chartItems: any
    setChartItems: any
    chartMode: IChartMode
    ctx: any
    sortedComparedRows: any
    selectedRateOfReturnOptions: string[]
}> = ({
    chartData,
    language,
    chartItems,
    setChartItems,
    chartMode,
    ctx,
    sortedComparedRows,
    selectedRateOfReturnOptions,
}) => {
    const {t} = useTranslation()
    const [excludedDays, setExcludedDays] = useState<Array<Date>>()
    const chartRef = useRef<HTMLDivElement | null>(null)
    const globalCtx = useContext(GlobalContext)
    const sessionCtx: SessionContextType = useContext(SessionContext)
    const chartItemLimit = 5
    const chartCurrency = chartData.currency.length > 1 || chartMode !== 'profile' ? 'PLN' : chartData.currency[0]
    const pdfLayout = chartMode !== 'profile' ? globalCtx.layout : chartData.data[0].kind

    const [fundChartData, setFundChartData] = useState<FundChartData[]>([])

    const [colors, setColors] = useState<Array<string>>([])
    const [isPdfLoading, setIsPdfLoading] = useState(false)

    const defaultColors: Array<string> = [
        '#003781',
        '#E86427',
        '#1E8927',
        '#C13584',
        '#EFBE25',
        '#1074A3',
        '#FB3F6B',
        '#168DC0',
        '#6ED39B',
        '#1FAFDB',
    ]

    const [calendarMinDate, setCalendarMinDate] = useState<Date | undefined>(undefined)
    const [calendarMaxDate, setCalendarMaxDate] = useState<Date | undefined>(undefined)
    const [startDate, setStartDate] = useState<Date | d3.NumberValue>(0)
    const [endDate, setEndDate] = useState<Date | d3.NumberValue>(0)
    const [startDateB, setStartDateB] = useState<Date>()
    const [endDateB, setEndDateB] = useState<Date>()
    const [periods, setPeriods] = useState<Periods>({
        one_year: undefined,
        one_day: undefined,
        one_month: undefined,
        three_years: undefined,
        three_months: undefined,
        five_years: undefined,
        six_months: undefined,
        max: undefined,
        year_to_date: undefined,
    })
    const [activeTab, setActiveTab] = useState<string>('rateOfReturnTab')
    const [standardizationInfo, setStandardizationInfo] = useState<boolean>(false)
    const [isAutoSwitchPeriodInfo, setIsAutoSwitchPeriodInfo] = useState<boolean>(false)
    const [activePeriod, setActivePeriod] = useState<string>('MAX')

    const bottomTxtColor: string = '#808080'
    const mainTxtColor: string = '#808080'
    const bottomTickColor: string = '#e0e0e0'
    const mainTickColor: string = '#e0e0e0'

    const pDateF = d3.timeParse('%Y-%m-%d %H:%M')
    const units: object = {not: {unit: chartCurrency, multiplier: 1}, stz: {unit: '%', multiplier: 100}}
    const d3pl = d3.formatLocale({
        decimal: ',',
        thousands: ' ',
        grouping: [3],
        currency: [chartCurrency, ''],
    })
    const d3pl_time: TimeLocaleDefinition = {
        dateTime: '%x, %X',
        date: '%-m/%-d/%Y',
        time: '%-I:%M:%S %p',
        periods: ['AM', 'PM'],
        days: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
        shortDays: ['Nd', 'Pon', 'Wt', 'Śro', 'Czw', 'Pt', 'Sob'],
        months: [
            'Styczeń',
            'Luty',
            'Marzec',
            'Kwiecień',
            'Maj',
            'Czerwiec',
            'Lipiec',
            'Sierpień',
            'Wrzesień',
            'Październik',
            'Listopad',
            'Grudzień',
        ],
        shortMonths: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paz', 'Lis', 'Gru'],
    }

    const curveStyle = d3.curveLinear
    const margin = {left: 0, right: 0, top: 0, bottom: 0}
    const sizeArr: number = 15

    // const layout = chartMode

    const buildFund = (item: FundProfile | undefined): FundChartData | undefined =>
        !item
            ? undefined
            : {
                  fundCode: item.id,
                  name: item.name,
                  currency: item.currency,
                  fractionalDigits: item.fractionalDigits,
                  quotation: item.valuations,
              }

    const getFund = useCallback(
        (code: string): FundChartData | undefined => {
            return buildFund(chartData?.data.find((item: FundProfile) => item?.id === code))
        },
        [chartData]
    )

    useEffect(() => {
        if (ctx.holiday.length > 0) {
            setExcludedDays(ctx.holiday)
        }
    }, [ctx.holiday])

    useEffect(() => {
        const key = findPeriodKey(startDateB as Date, endDateB as Date)
        if (key === null) {
            setActivePeriod('')
        } else {
            const item = periodMap.find((item) => item.value === key && item.label !== undefined)
            if (item !== undefined) {
                setActivePeriod(item.label)
            }
        }
    }, [startDateB, endDateB])

    useEffect(() => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`)
    }, [])

    const [domainInterval, setDomainInterval] = useState({min: null, max: null})
    const getChartDomain = useCallback(
        (activeFunds) => {
            let minCommonDate: Date | null = null
            let maxCommonDate: Date | null = null

            activeFunds.forEach((item: string) => {
                // console.log(item)
                const fund = getFund(item)
                if (fund) {
                    const minFundDate = new Date(fund.quotation[0].date)
                    const maxFundDate = new Date(fund.quotation[fund.quotation.length - 1].date)
                    if (minCommonDate === null) minCommonDate = minFundDate
                    else minCommonDate = minCommonDate < minFundDate ? minCommonDate : minFundDate

                    if (maxCommonDate === null) maxCommonDate = maxFundDate
                    else maxCommonDate = maxCommonDate > maxFundDate ? maxCommonDate : maxFundDate
                }
            })
            return {min: minCommonDate, max: maxCommonDate}
            // if (minCommonDate && maxCommonDate){
            //     setDomainInterval({min: minCommonDate, max: maxCommonDate})
            //     setStartDateB(minCommonDate)
            //     setEndDateB(maxCommonDate)
            // }
        },
        [chartData]
    )

    const getFundColor = useCallback(
        (fundCode: string): string => {
            return colors[chartItems?.indexOf(fundCode)]
            // let i = 0
            // let found = ''
            // fundChartData.map((fundItem) => {
            //     if (found == '')
            //         if (chartItems.includes(fundItem.fundCode))
            //             if (fundItem.fundCode === fundCode) found = defaultColors[i]
            //             else i++
            // })
            // return found
        },
        [chartItems, fundChartData, colors]
    )

    useEffect(() => {
        if (language.includes('pl')) d3.timeFormatDefaultLocale(d3pl_time)
        if (chartItems.length > 0) {
            const newColors: string[] = []
            for (let i = 0; i < chartItems.length; i++) {
                const availableColor = defaultColors.find(
                    (color) => !newColors.includes(color) && !colors.includes(color)
                )
                if (availableColor) {
                    newColors.push(availableColor)
                }
            }
            setColors(newColors)
        }
    }, [])

    const getXAxisFormat = useCallback(() => {
        if (startDateB && endDateB) {
            const monthDiff: number = differenceInMonths(endDateB, startDateB)
            if (monthDiff > 48) {
                return '%Y'
            } else if (monthDiff <= 48 && monthDiff > 3) {
                return "%b '%g"
            } else {
                return '%Y-%m-%d'
                // return '%Y-%m-%d %H:%M:%S'
            }
        } else {
            return '%Y'
        }
    }, [startDateB, endDateB])

    const findPeriodKey = (startDateB: Date, endDateB: Date): string | null => {
        for (const key in periods) {
            const period = periods[key as keyof Periods]
            if (period) {
                // Check if period is defined
                const periodBegin = new Date(period.begin)
                const periodEnd = new Date(period.end)
                if (periodBegin.getTime() === startDateB.getTime() && periodEnd.getTime() === endDateB.getTime()) {
                    return key // Return the key if both dates match
                }
            }
        }
        return null // Return null if no match is found
    }

    const handlePeriodClick = (value: IPeriodValues): void => {
        const key: string = value
        if (periods[key as keyof Periods] !== undefined) {
            // @ts-expect-error for sure
            setStartDateB(new Date(periods[key as keyof Periods].begin))
            // @ts-expect-error for sure
            setEndDateB(new Date(periods[key as keyof Periods].end))
        }
    }

    useEffect(() => {
        if (!isEmpty(chartData)) {
            setStartDate(new Date(chartData.begin))
            setEndDate(new Date(chartData.end))
            setCalendarMinDate(new Date(chartData.begin))
            setCalendarMaxDate(new Date(chartData.end))
            // setPeriods(chartData.relativePeriods)

            const domain = getChartDomain(chartItems)
            if (domain.min && domain.max) {
                setDomainInterval(domain)
                setPeriods(getPeriodsForDate(domain))
                setStartDateB(domain.min)
                setEndDateB(domain.max)
            } else {
                setDomainInterval({min: new Date(chartData.begin), max: new Date(chartData.end)})
                setStartDateB(new Date(chartData.begin))
                setEndDateB(new Date(chartData.end))
            }
        }

        if (chartData && chartData.data.length > 0) {
            const updatedFundChartData: FundChartData[] = chartData.data?.map((item) => buildFund(item))

            const allStartDates = new Set()
            updatedFundChartData.forEach((item) => {
                if (item) allStartDates.add(item.quotation[0].date)
            })

            if (allStartDates.size > 1) {
                setStandardizationInfo(true)
            }

            setFundChartData(sortRows(updatedFundChartData, {sortCol: 'name', sortDir: 'ASC'}, language, []))

            if (chartItems.length > 0) {
                const newColors: string[] = []
                for (let i = 0; i < chartItems.length; i++) {
                    const availableColor = defaultColors.find(
                        (color) => !newColors.includes(color) && !newColors.includes(color)
                    )
                    if (availableColor) {
                        newColors.push(availableColor)
                    }
                }
                setColors(newColors)
            }
        }
    }, [chartData])

    useEffect(() => {
        if (startDateB) {
            let showStandardizationWarning = false
            const updatedFundChartData = chartData.data?.map((item) => buildFund(item))
            const allStartDates = new Set()
            updatedFundChartData.forEach((item) => {
                if (item) allStartDates.add(item.quotation[0].date)
            })

            for (const x of Array.from(allStartDates) as Array<string>) {
                if (format(startDateB as Date, 'yyyy-MM-dd') < x) {
                    showStandardizationWarning = true
                    break
                }
            }

            setStandardizationInfo(showStandardizationWarning)
        }
    }, [startDateB])

    const getPeriodsForDate = useCallback(
        (domain: {max: Date | null; min: Date | null}) => {
            const blueprint = {
                one_year: undefined,
                one_day: undefined,
                one_month: undefined,
                three_years: undefined,
                three_months: undefined,
                five_years: undefined,
                six_months: undefined,
                max: undefined,
                year_to_date: undefined,
            }

            if (!domain) {
                return blueprint
            }

            if (!chartData.relativePeriods[format(domain.max as Date, 'yyyy-MM-dd')]) {
                return blueprint
            }

            const periodForDate = structuredClone(chartData.relativePeriods[format(domain.max as Date, 'yyyy-MM-dd')])

            periodForDate.max.begin = format(domain.min as Date, 'yyyy-MM-dd')

            for (const givenPeriod in periodForDate) {
                if (domain.min && Date.parse(periodForDate[givenPeriod]?.begin) < domain.min.getTime())
                    periodForDate[givenPeriod] = undefined
            }

            return periodForDate
        },
        [chartData]
    )

    useEffect(() => {
        const handleResize = () => {
            drawChart(
                chartRef.current.clientWidth,
                chartRef.current.clientHeight,
                '.aol-fund-profile-chart-content',
                'mainSvg'
            )
        }
        window.addEventListener('resize', handleResize)

        drawChart(
            chartRef.current.clientWidth,
            chartRef.current.clientHeight,
            '.aol-fund-profile-chart-content',
            'mainSvg'
        )
        // drawChart(780, 400, '.pdf-chart', 'pdfSvg', false)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [
        activeTab,
        startDate,
        endDate,
        startDateB,
        endDateB,
        colors,
        // d3pl,
        // margin,
        fundChartData,
        chartItems,
    ])

    // chart reRenders debug
    // useEffect(() => console.log('activeTab', activeTab), [activeTab]);
    // useEffect(() => console.log('startDate', startDate), [startDate]);
    // useEffect(() => console.log('endDate', endDate), [endDate]);
    // useEffect(() => console.log('startDateB', startDateB), [startDateB]);
    // useEffect(() => console.log('endDateB', endDateB), [endDateB]);
    // useEffect(() => console.log('colors', colors), [colors]);
    // useEffect(() => console.log('fundChartData', fundChartData), [fundChartData]);
    // useEffect(() => console.log('chartItems', chartItems), [chartItems]);

    interface chartOptions {
        isBottomChart: boolean
        fontSize: string
    }

    const drawChart = useCallback(
        (
            chartWidth: number,
            chartHeight: number,
            selectorForDraw: string,
            mainChartId: string,
            options: chartOptions | any = {}
        ) => {
            const defaultOptions: chartOptions = {
                fontSize: '11px',
                isBottomChart: true,
            }
            options = {...defaultOptions, ...options}

            if (chartWidth <= 0 || chartHeight <= 0) return
            const mode = activeTab === 'rateOfReturnTab' ? 'stz' : 'not'
            const tooltipMarginRect = activeTab === 'rateOfReturnTab' ? 2 : 6
            const tooltipMargin = activeTab === 'rateOfReturnTab' ? -2 : 4
            const modebot = activeTab === 'rateOfReturnTab' ? 'stzbot' : 'notbot'
            const dane: object = {
                inv: {},
                not: {},
                stz: {},
                notbot: {},
                stzbot: {},
            }
            const initMinMax: number = activeTab === 'rateOfReturnTab' ? 0 : 1e10

            let xm: any
            let ym: any
            let brush: d3.BrushBehavior<unknown>
            const pth: any = []
            let xAxism: any
            let yAxism: any
            let ymmin: number = initMinMax
            let ymmax: number = -initMinMax
            let ydmin: number = initMinMax
            let ydmax: number = -initMinMax
            const accuracy: object = {stz: 2, not: 2}

            const bottomBoxSize: number = 0.08
            const bottomBSize: number = 0.03

            let a, b, c, d

            function isFundDrawingNeeded(fund: FundChartData): boolean {
                const fundStartDateB = new Date(fund.quotation[0].date)
                const fundEndDateB = new Date(fund.quotation[fund.quotation.length - 1].date)

                return areIntervalsOverlapping(
                    {
                        start: startDateB,
                        end: endDateB,
                    },
                    {
                        start: fundStartDateB,
                        end: fundEndDateB,
                    }
                )
            }

            fundChartData.forEach((fundItem) => {
                const isMainDraw: boolean = isFundDrawingNeeded(fundItem)
                const isSelected: boolean = chartItems.includes(fundItem.fundCode)
                if (fundItem.quotation !== null) {
                    let stzwp: number = 0
                    if (isMainDraw) {
                        dane['inv'][fundItem.fundCode] = []
                        dane['not'][fundItem.fundCode] = []
                        dane['stz'][fundItem.fundCode] = []
                    }
                    dane['notbot'][fundItem.fundCode] = []
                    dane['stzbot'][fundItem.fundCode] = []
                    if (isMainDraw) {
                        const filteredQuotations = fundItem.quotation.filter(
                            (record) => new Date(record.date) >= startDateB
                        )
                        filteredQuotations.forEach((record: any, counter: number) => {
                            // const [, record] = item
                            const data = pDate(record.date)
                            if (stzwp === 0) {
                                stzwp = record.value
                            }
                            if (
                                fundItem.currency !== 'PLN' &&
                                chartMode !== 'profile' &&
                                record.value_pln !== undefined
                            ) {
                                dane['not'][fundItem.fundCode][counter] = [data, record.value_pln]
                            } else dane['not'][fundItem.fundCode][counter] = [data, record.value]

                            dane['stz'][fundItem.fundCode][counter] = [data, record.value / stzwp - 1]
                            dane['inv'][fundItem.fundCode][record.date] = counter
                        })
                    }
                    stzwp = 0

                    Object.entries(fundItem.quotation).forEach(([, record]: [string, Quotation], counter: number) => {
                        const data = pDate(record.date)

                        if (stzwp === 0) {
                            stzwp = record.value
                        }

                        if (fundItem.currency !== 'PLN' && chartMode !== 'profile' && record.value_pln !== undefined) {
                            dane['notbot'][fundItem.fundCode][counter] = [data, record.value_pln]
                        } else dane['notbot'][fundItem.fundCode][counter] = [data, record.value]

                        dane['stzbot'][fundItem.fundCode][counter] = [data, record.value / stzwp - 1]
                    })

                    if (isMainDraw) {
                        ;[c, d] = rMinMax(
                            dane[mode][fundItem.fundCode].filter(function (d: Array<Date>) {
                                return d[0] >= startDateB! && d[0] <= endDateB!
                            }),
                            (d: any) => {
                                return d[1]
                            }
                        )
                        if (isSelected || chartItems.length === 0) {
                            ymmin = Math.min(ymmin, c)
                            ymmax = Math.max(ymmax, d)
                        }
                    }

                    ;[a, b] = rMinMax(dane[modebot][fundItem.fundCode], (d: any) => {
                        return d[1]
                    })
                    if (isSelected || chartItems.length === 0) {
                        ydmin = Math.min(ydmin, a)
                        ydmax = Math.max(ydmax, b)
                    }
                }
            })
            // console.log(startDateB, endDateB)
            // console.log(chartItems, dane)
            // console.log(dane)
            // adjust main chart y-axis limits and accuracy for visible funds
            if (ymmax - ymmin === 0) {
                ymmax = ymmax + 1
                ymmin = ymmin - 1
            } else if (ymmax - ymmin < 0.044) {
                // accuracy.stz = 2
                ymmax = ymmax + 0.001
                ymmin = ymmin - 0.001
            } else {
                // accuracy.stz = 0
            }

            if (ydmin - ydmax === 0) {
                ydmin = ydmin + 1
                ydmax = ydmax - 1
            }

            const heightMain = chartHeight * (1 - bottomBoxSize - bottomBSize)
            const heightBottom = chartHeight * bottomBoxSize
            const widthMain = chartWidth - margin.left - margin.right
            const widthBottom = widthMain
            let sel: any = null
            let onInit: boolean = true

            const svg = addSvg(chartWidth, chartHeight, selectorForDraw)
            // const pdfSvg = addSvg(780, 300, '.pdf-chart')

            postSvgActions(svg, options.isBottomChart)

            // postSvgActions(pdfSvg, false)

            function postSvgActions(svg, isBottom: boolean = true) {
                const svgMain = addBox(svg, margin.left, margin.top)
                const svgBottom = addBox(svg, margin.left + sizeArr, margin.top + heightMain)

                if (isBottom) {
                    addBG(svgBottom, 'arrl', 1 - sizeArr, heightBottom - 2, sizeArr)
                    addBG(svgBottom, 'arrp', widthBottom - 2 * sizeArr, heightBottom - 2, sizeArr - 2)

                    // Dolny wykres
                    const xb = d3.scaleTime().range([0, widthBottom - 2 * sizeArr])
                    const yb = d3.scaleLinear().range([heightBottom, 0])
                    // xb.domain([startDate, endDate])
                    xb.domain([domainInterval.min, domainInterval.max])
                    yb.domain([ydmin, ydmax])

                    Object.entries(dane[modebot]).forEach((fund: any, fundIndex) => {
                        const [code, fundData] = fund

                        // ??? .attr('stroke', colors[chartItems.indexOf(fund[0])])
                        svgBottom
                            .append('path')
                            .data([fundData])
                            .attr('class', 'line')
                            .attr('stroke-width', '1')
                            .attr('fill', 'none')
                            .attr('stroke', getFundColor(code))
                            .attr(
                                'd',
                                d3
                                    .line()
                                    .x((d: any) => {
                                        return xb(d[0])
                                    })
                                    .y((d: any) => {
                                        return yb(d[1])
                                    })
                            )
                    })

                    Object.entries(dane[modebot]).forEach((fund: any, fundIndex: number) => {
                        // @ts-ignore
                        const [code, fundData] = fund
                        if (chartItems.includes(code)) {
                            svgBottom
                                .append('path')
                                .data([fundData])
                                .attr('class', 'area')
                                .attr('stroke-width', '0')
                                .attr('stroke', 'transparent')
                                .attr('opacity', '0.05')
                                .attr('fill', getFundColor(code))
                                .attr(
                                    'd',
                                    d3
                                        .area()
                                        .x((d: any) => {
                                            return xb(d[0])
                                        })
                                        .y0(() => {
                                            return yb(ydmin)
                                        })
                                        .y1((d: any) => {
                                            return yb(d[1])
                                        })
                                )
                        }
                    })

                    svgBottom
                        .append('g')
                        .attr('transform', 'translate(0,' + heightBottom + ')')
                        .attr('id', 'tickBottom')
                        .call(
                            d3
                                .axisBottom(xb)
                                .tickFormat((d: any) => d3.timeFormat('%Y')(d))
                                .ticks(4)
                        )
                        .call(() => {
                            d3.selectAll('#tickBottom .tick text')
                                .attr('text-anchor', 'middle')
                                .attr('dy', -12)
                                .attr('dx', 2)
                                .attr('fill', bottomTxtColor)
                                .style('font-size', options.fontSize)

                            d3.selectAll('#tickBottom .tick:first-of-type text').attr('text-anchor', 'start')
                            d3.selectAll('#tickBottom .tick:last-of-type text').attr('text-anchor', 'end')

                            d3.selectAll('#tickBottom .tick line')
                                .attr('stroke', bottomTickColor)
                                .attr('y1', -heightBottom)
                                .attr('y2', 0)

                            d3.selectAll('#tickBottom .domain')
                                .attr('stroke', bottomTickColor)
                                .attr('transform', 'translate(0,-' + heightBottom + ')')
                        })

                    brush = d3
                        .brushX()
                        .extent([
                            [0, 0],
                            [widthBottom - 2 * sizeArr, heightBottom + chartHeight * bottomBSize],
                        ])
                        .on('start brush end', ({selection}) => {
                            const ndbr = d3.select('#mainBrush')
                            if (ndbr.attr('pointer-events') === 'all' && !onInit) {
                                setStartDateB(xb.invert(selection[0]))
                                setEndDateB(xb.invert(selection[1]))
                            }
                            ndbr.call((g, selection) => {
                                sel = selection

                                g.selectAll('.handle--custom')
                                    .data([{type: 'w'}, {type: 'e'}])
                                    .join((enter) =>
                                        enter
                                            .append('circle')
                                            .attr('class', 'handle--custom')
                                            .attr('fill', '#ffffff')
                                            .attr('stroke', '#2976de')
                                            .attr('stroke-width', 2)
                                            .attr('cursor', 'ew-resize')
                                            .attr('cx', -0.5)
                                            .attr('cy', 1)
                                            .attr('r', 3)
                                    )
                                    .attr('display', selection === null ? 'none' : null)
                                    .attr(
                                        'transform',
                                        selection === null ? null : (d, i) => `translate(${selection[i]},20)`
                                    )

                                g.selectAll('.handle--line')
                                    .data([{type: 'a'}, {type: 'b'}, {type: 'c'}])
                                    .join((enter) =>
                                        enter
                                            .insert('line', ':first-child')
                                            .attr('class', 'handle--line')
                                            .attr('stroke', '#7f7f7f')
                                            .attr('stroke-width', 1.5)
                                            .attr('y1', 33)
                                            .attr('y2', 40)
                                    )
                                    .attr('display', selection === null ? 'none' : null)
                                    .attr(
                                        'x1',
                                        selection === null
                                            ? null
                                            : // @ts-ignore
                                              (d, i) => `${(selection[1] + selection[0]) / 2 + (i - 1) * 3}`
                                    )
                                    .attr(
                                        'x2',
                                        selection === null
                                            ? null
                                            : // @ts-ignore
                                              (d, i) => `${(selection[1] + selection[0]) / 2 + (i - 1) * 3}`
                                    )
                                g.selectAll('.handle--selection')
                                    .data([{type: 's'}])
                                    .join((enter) =>
                                        enter
                                            .insert('rect', ':first-child')
                                            .attr('class', 'handle--selection')
                                            .attr('fill', '#cccccc')
                                            .attr('stroke', '#cccccc')
                                            .attr('cursor', 'move')
                                            .attr('y', 30)
                                            .attr('height', 12)
                                    )
                                    .attr('display', selection === null ? 'none' : null)
                                    .attr('x', selection === null ? null : () => `${selection[0]}`)
                                    .attr('width', selection === null ? null : () => `${selection[1] - selection[0]}`)
                            }, selection)
                        })

                    brush.handleSize(1)

                    d3.selectAll('#arrl').on('mousedown', () =>
                        d3.select('.brush').call(brush.move, sel[0] > 0 ? [sel[0] - 1, sel[1] - 1] : [sel[0], sel[1]])
                    )

                    d3.selectAll('#arrp').on('mousedown', () =>
                        d3
                            .select('.brush')
                            .call(
                                brush.move,
                                sel[1] + 1 < widthBottom - 2 * sizeArr ? [sel[0] + 1, sel[1] + 1] : [sel[0], sel[1]]
                            )
                    )

                    svgBottom
                        .append('g')
                        .attr('class', 'brush')
                        .attr('id', 'mainBrush')
                        .call(brush)
                        .call(() => {
                            d3.selectAll('#mainBrush .handle').attr('fill', '#a0a0a0')
                            d3.selectAll('#mainBrush .selection')
                                .attr('fill', '#d0d0d0')
                                .attr('stroke', '#d0d0d0')
                                .attr('style', 'padding-top: 3')

                            if (startDateB instanceof Date && endDateB instanceof Date) {
                                d3.selectAll('.brush')
                                    .call(brush.move, [xb(startDateB), xb(endDateB)])
                                    .call(() => {
                                        onInit = false
                                    })
                            }
                        })
                }

                // Górny wykres
                xm = d3.scaleTime().range([0, widthMain])
                ym = d3.scaleLinear().range([heightMain - 40, 0])
                xm.domain([startDateB, endDateB])
                ym.domain([ymmin, ymmax])

                svgMain
                    .append('rect')
                    .attr('x', -margin.left)
                    .attr('y', 0)
                    .attr('width', margin.left)
                    .attr('height', chartHeight)
                    .attr('fill', '#ffffff')

                xAxism = svgMain
                    .append('g')
                    .attr('id', 'tickMainX')
                    .attr('transform', 'translate(0,' + (heightMain - 30) + ')')

                let xMainTicks: number
                if (
                    differenceInDays(endDateB as Date, startDateB as Date) === 1 ||
                    differenceInDays(endDateB as Date, startDateB as Date) === 2
                ) {
                    xMainTicks = 2
                } else if (
                    differenceInDays(endDateB as Date, startDateB as Date) > 2 &&
                    differenceInDays(endDateB as Date, startDateB as Date) <= 30
                ) {
                    xMainTicks = 4
                } else {
                    xMainTicks = 8
                }

                xAxism
                    .call(
                        d3
                            .axisBottom(xm)
                            .tickFormat((d: any) => d3.timeFormat(getXAxisFormat())(d))
                            .ticks(xMainTicks)
                    )
                    .call(() => {
                        d3.selectAll('#tickMainX .tick text')
                            .attr('text-anchor', 'middle')
                            .attr('dy', 8)
                            .attr('fill', mainTxtColor)
                            .style('font-size', options.fontSize)
                        d3.selectAll('#tickMainX .tick line').attr('stroke', mainTickColor)
                        d3.selectAll('#tickMainX .domain').attr('stroke', mainTickColor)
                    })

                if (
                    differenceInDays(endDateB as Date, startDateB as Date) > 2 &&
                    differenceInDays(endDateB as Date, startDateB as Date) <= 30
                ) {
                    d3.selectAll('#tickMainX .tick:first-of-type text').attr('text-anchor', 'start')
                    d3.selectAll('#tickMainX .tick:last-of-type text').attr('text-anchor', 'end')
                }
                // if (differenceInDays(endDateB as Date, startDateB as Date) === 1) {
                //
                //     // d3.selectAll('#tickMainX .tick:first-of-type').attr('transform', 'translate(40,0)')
                // }

                yAxism = svgMain.append('g').attr('id', 'tickMainY')
                yAxism
                    .call(
                        d3
                            .axisRight(ym)
                            .tickSize(widthMain)
                            .tickFormat((d: any) =>
                                //check if y label fits - if so format - else return ''
                                ym(d) - 2 > margin.top + 6
                                    ? d3pl.format('.' + accuracy[mode] + 'f')(d * units[mode].multiplier) +
                                      ' ' +
                                      units[mode].unit
                                    : ''
                            )
                            .ticks(6)
                    )
                    .call(() => {
                        d3.selectAll('#tickMainY .tick text')
                            .attr('text-anchor', 'end')
                            .attr('x', widthMain - 5)
                            .attr('dy', -2)
                            // if label doesn't fit - draw text under line
                            // .attr('dy', (d) => {
                            //     return ym(d) - 2 < margin.top + 6 ? 12 : -2
                            // })
                            .attr('fill', mainTxtColor)
                            .style('font-size', options.fontSize)
                        d3.selectAll('#tickMainY .tick line').attr('stroke', mainTickColor)
                        d3.selectAll('#tickMainY .domain').remove()
                    })

                // Ta część rysuje nowy wykres
                Object.entries(dane[mode]).forEach((fund: any) => {
                    const [fundCode, fundData] = fund
                    if (chartItems.includes(fundCode)) {
                        pth[fundCode] = svgMain
                            .append('path')
                            .data([fundData])
                            .attr('class', 'line')
                            .attr('stroke-width', '2')
                            .attr('fill', 'none')
                            .attr('stroke', getFundColor(fundCode))
                            .attr(
                                'd',
                                d3
                                    .line()
                                    .x((d: any) => {
                                        return xm(d[0])
                                    })
                                    .y((d) => {
                                        return ym(d[1])
                                    })
                                    .curve(curveStyle)
                            )
                    }
                })

                // Chart tooltips
                const legW: number = 88
                const legH: number = 18
                const legT: number = 6
                const posY: number = 110
                const pointerGroup = svgMain.append('g').attr('transform', 'translate(-1000,' + margin.top + ')')
                const pointerLine = pointerGroup.append('line')

                const dateTooltip = pointerGroup.append('g').attr('id', 'date-tooltip-group')
                const dateTooltipRect = dateTooltip.append('path')
                const dateTooltipText = dateTooltip.append('text')

                const valueTooltip = pointerGroup.append('g').attr('id', 'value-tooltip-group')
                const valueTooltipRect = valueTooltip.append('path')
                const valueTooltipText = valueTooltip.append('text')

                pointerLine
                    .attr('x1', 0)
                    .attr('y1', 0)
                    .attr('x2', 0)
                    .attr('y2', heightMain - 20) //triangle: heightMain - 30
                    .attr('stroke-width', 1)
                    .attr('stroke', '#505050')

                dateTooltipRect
                    .attr(
                        'd',
                        `M ${-legW / 2} ${heightMain - 30 + legH + legT} ` +
                            `L ${-legW / 2} ${heightMain - 30 + legT} ` +
                            `L ${-legT} ${heightMain - 30 + legT} ` +
                            `L0 ${heightMain - 24} ` + //triangle: heightMain - 30
                            `L ${legT} ${heightMain - 30 + legT} ` +
                            `L ${legW / 2} ${heightMain - 30 + legT} ` +
                            `L ${legW / 2} ${heightMain - 30 + legH + legT} Z`
                    )
                    .attr('stroke-width', 1)
                    .attr('stroke', '#505050')
                    .attr('fill', '#ffffff')

                valueTooltipRect.attr('stroke-width', 1).attr('stroke', '#505050').attr('fill', '#ffffff')

                dateTooltipText
                    .attr('class', 'date-tooltip')
                    .attr('text-anchor', 'middle')
                    .attr('x', 0)
                    .attr('y', heightMain - 30 + legH + 1)

                svg.append('rect')
                    .attr('id', mainChartId)
                    .attr('x', margin.left)
                    .attr('y', margin.top)
                    .attr('width', chartWidth - margin.left)
                    .attr('width', chartWidth - margin.left)
                    .attr('height', heightMain - margin.top)
                    .attr('fill', 'transparent')
                    .attr('stroke-width', '1')

                d3.selectAll('#' + mainChartId)
                    .on('mousemove', (d: any) => {
                        const xPos: number =
                            d.clientX - document.getElementById(mainChartId)!.getBoundingClientRect().left
                        const dateAtXPos: Date = xm.invert(xPos)
                        let dateStr: string = d3.timeFormat('%Y-%m-%d')(dateAtXPos)
                        let xTooltipShift = 0

                        // Clear previous tooltip content
                        valueTooltip.selectAll('*').remove()

                        let cf: number = 0
                        Object.entries(dane[mode]).forEach((fund: any) => {
                            const [fundCode, fundData] = fund

                            // Adjust dateStr to find the correct index
                            for (let i: number = 0; i < 9; i++) {
                                if (Object.prototype.hasOwnProperty.call(dane['inv'][fundCode], dateStr)) break
                                dateStr = d3.timeFormat('%Y-%m-%d')(d3.timeDay.offset(new Date(dateStr), -1))
                            }

                            const index = dane['inv'][fundCode][dateStr]
                            if (chartItems.length > 0 && chartItems.includes(fundCode)) {
                                if (Object.prototype.hasOwnProperty.call(fundData, index)) {
                                    // Create a new group for each fund's tooltip entry
                                    const fundGroup = valueTooltip
                                        .append('g')
                                        .attr('transform', `translate(0, ${posY - 5 + 18 * cf})`)

                                    if (xPos < 89) xTooltipShift = 89

                                    // Append a square (rectangle) instead of a circle
                                    fundGroup
                                        .append('rect')
                                        .attr('x', -76 -tooltipMarginRect + xTooltipShift) // Adjust x position
                                        .attr('y', -7) // Adjust y position (centered vertically)
                                        .attr('width', 6) // Set width of the square
                                        .attr('height', 6) // Set height of the square
                                        .attr('fill', getFundColor(fundCode)) // Fill color

                                    fundGroup
                                        .append('text')
                                        .attr('class', 'value-tooltip')
                                        .attr('text-anchor', 'start')
                                        .attr('x', -legW + 20 - tooltipMargin + xTooltipShift)
                                        .attr('y', 0)
                                        .html(
                                            d3pl.format('.' + accuracy[mode] + 'f')(
                                                fundData[index][1] * units[mode].multiplier
                                            ) +
                                                ' ' +
                                                units[mode].unit
                                        )

                                    cf += 1

                                    // mainChart/bottom tooltip position - far left and far right position
                                    const xDateTooltipMaxPos = chartWidth - 46
                                    if (xPos < 44) {
                                        dateTooltip.attr(
                                            'transform',
                                            'translate(' + (-xPos + 44) + ',' + margin.top + ')'
                                        )
                                    } else if (xPos > xDateTooltipMaxPos) {
                                        dateTooltip.attr(
                                            'transform',
                                            'translate(' + (-xPos + xDateTooltipMaxPos) + ',' + margin.top + ')'
                                        )
                                    } else {
                                        dateTooltip.attr('transform', 'translate(' + 0 + ',' + margin.top + ')')
                                    }
                                }
                            }
                        })

                        if (chartItems.length > 0) {
                            // Update tooltip height based on the number of entries
                            const tooltipHeight = cf * 18 + fundChartData.length
                            valueTooltip.attr('height', tooltipHeight)

                            // Add a background rectangle for the tooltip
                            valueTooltip
                                .insert('rect', ':first-child') // Insert rectangle before other elements
                                .attr('x', -legW + xTooltipShift) // Adjust x position
                                .attr('y', posY - 20) // Adjust y position
                                .attr('width', legW) // Set width (adjust as needed)
                                .attr('height', tooltipHeight) // Set height
                                .attr('fill', 'rgba(255, 255, 255, 0.8)') // Background color with some transparency
                                .attr('stroke', '#505050') // Border color
                                .attr('stroke-width', 1) // Border thickness
                        }

                        // Update pointer position
                        pointerGroup.attr('transform', 'translate(' + (xPos + 1) + ',' + margin.top + ')')
                        dateTooltipText.html(dateStr)
                    })
                    .on('mouseout', () => {
                        pointerGroup.attr('transform', 'translate(-1000,' + margin.top + ')')
                    })
            }

            // console.log('Rysuję...')
        },
        [activeTab, startDate, endDate, startDateB, endDateB, colors, fundChartData, chartItems, domainInterval]
    )

    // Data d3 dla wykresu
    const pDate = (d: string): any => {
        if (d.length === 10) {
            d = d + ' 00:00'
        }
        return pDateF(d)
    }

    const addSvg = (chartWidth: number, chartHeight: number, selector: string) => {
        // Remove chart content
        d3.select(selector).selectChildren('*').remove()

        // Create fresh SVG element
        return d3
            .select(selector)
            .append('svg')
            .attr('width', chartWidth)
            .attr('height', chartHeight)
            .attr('pointer-events', 'all')
    }

    const addBox = (svg: any, left: number, top: number) => {
        return svg.append('g').attr('transform', 'translate(' + left + ',' + top + ')')
    }

    const addBG = (svg: any, id: any, x: number, y: number, s: number) => {
        svg = svg.append('g').attr('id', id)
        svg.append('rect')
            .attr('id', id)
            .attr('x', x)
            .attr('y', y + 1)
            .attr('width', s - 2)
            .attr('height', s - 1)
            .attr('stroke', '#b0b0b0')
            .attr('stroke-opacity', 0.5)
            .attr('stroke-width', '1')
            .attr('fill', '#e0e0e0')

        return x > 400
            ? svg
                  .append('path')
                  .attr('fill', '#707070')
                  .attr('fillOpacity', 0.5)
                  .attr('stroke', 'none')
                  .attr(
                      'd',
                      'M' +
                          (x + 4) +
                          ',' +
                          (y + 4) +
                          'L' +
                          (x + s - 5) +
                          ',' +
                          (y + s / 2) +
                          'L' +
                          (x + 4) +
                          ',' +
                          (y + s - 3) +
                          'Z'
                  )
            : svg
                  .append('path')
                  .attr('fill', '#707070')
                  .attr('fillOpacity', 0.5)
                  .attr('stroke', 'none')
                  .attr(
                      'd',
                      'M' +
                          (x + s - 6) +
                          ',' +
                          (y + 4) +
                          'L' +
                          (x + 3) +
                          ',' +
                          (y + s / 2) +
                          'L' +
                          (x + s - 6) +
                          ',' +
                          (y + s - 3) +
                          'Z'
                  )
    }

    const rMinMax = (data: any, f: any, dz: number = 0.1) => {
        let min: any = d3.min(data, f)
        let max: any = d3.max(data, f)
        const d: number = max - min
        min = min - dz * d
        max = max + dz * d
        return [min, max]
    }

    const handleQuotationTab = (): void => {
        setActiveTab('quotationTab')
    }

    const handleRateOfReturnTab = (): void => {
        setActiveTab('rateOfReturnTab')
    }

    useEffect(
        function () {
            if (isPdfLoading) {
                setTimeout(function () {
                    handleDownloadBtn('pdf')
                }, 100)
            }
        },
        [isPdfLoading]
    )

    const handleDownloadBtn = (extension: string): void => {
        const fileDate = new Date()
        let filename: string
        if (globalCtx.layout == 'dfe') {
            filename = 'Allianz.pl - ' + t('label.xlsx.dfe') + ' - ' + fileDate.getTime()
        } else if (chartMode === 'compare') {
            filename = 'Allianz.pl - ' + t('label.xlsx.compare.filename') + ' - ' + fileDate.getTime()
        } else {
            filename = 'Allianz.pl - ' + fundChartData[0].name + ' - ' + fileDate.getTime()
        }

        if (extension == 'xlsx') {
            //Setting data
            // const csvData = []
            const fundNames = new Set()
            const fundCurrency = {}
            // quotation data transposition - for daily values
            const dailyQuota = []
            fundChartData.forEach((fund: FundChartData) => {
                if (chartItems.includes(fund.fundCode)) {
                    fundCurrency[fund.name] = fund.currency
                    fund.quotation?.forEach((row) => {
                        fundNames.add(fund.name)
                        const dateObj = new Date(row.date)
                        if (startDateB && endDateB && dateObj >= startDateB && dateObj <= endDateB) {
                            if (!!dailyQuota[row.date]) {
                                dailyQuota[row.date][fund.name] = row.value?.toFixed(2)
                            } else {
                                dailyQuota[row.date] = {}
                                dailyQuota[row.date][fund.name] = row.value?.toFixed(2)
                            }
                        }
                    })
                }
            })

            const transposedData = []
            Object.keys(dailyQuota).forEach((daily) => {
                const fundsValueFlatten = {
                    Data: daily,
                }
                fundNames.forEach((oneFundName) => {
                    fundsValueFlatten[t('label.xlsx.common.value') + ' ' + oneFundName] = '-'
                    fundsValueFlatten[t('label.xlsx.common.currency') + ' ' + oneFundName] = '-'
                })
                Object.keys(dailyQuota[daily]).forEach((fundValue, idx, array) => {
                    fundsValueFlatten[t('label.xlsx.common.value') + ' ' + fundValue] = dailyQuota[daily][fundValue]
                    fundsValueFlatten[t('label.xlsx.common.currency') + ' ' + fundValue] = fundCurrency[fundValue]
                    if (idx == array.length - 1) {
                        transposedData.push(fundsValueFlatten)
                    }
                })
            })
            transposedData.sort(({Data: a}, {Data: b}) => (a < b ? -1 : a > b ? 1 : 0))

            //Style XLSX
            const maxColNum = chartItems.length * 2 + 1
            let headerRowForFundData: number
            const ws = XLSX.utils.json_to_sheet([])
            ws['!merges'] = []

            let headerTitle: string
            if (globalCtx.layout == 'dfe') {
                headerTitle = t('label.xlsx.dfe') + ' - ' + t('label.xlsx.common.history')
            } else if (chartMode === 'compare') {
                headerTitle = t('label.xlsx.compare.filename')
            } else {
                headerTitle = fundChartData[0].name + ' - ' + t('label.xlsx.common.history')
            }

            const additionalRows = [{}, {A: headerTitle}, {A: getLabelGenerated(t, fileDate)}]

            if (chartMode === 'compare' || (chartMode === 'catalog' && globalCtx.layout === 'dfe')) {
                // XLSX- PORÓWNANIE - KILKA FUNDUSZY
                headerRowForFundData = 5

                //https://docs.sheetjs.com/docs/csf/features/rowprops/#row-heights
                ws['!rows'] = [{hpx: 25}, {hpx: 20}, {hpx: 20}, {hpx: 30}, {hpx: 30}]

                // row with fund names
                const rowWithNames = {A: null}
                let rwnI = 1

                const rowWithNamesNum = 4
                const styleForNames = {
                    font: {sz: 12, color: {rgb: '006192'}},
                    alignment: {horizontal: 'center', vertical: 'center'},
                }
                fundChartData.map((x) => {
                    if (chartItems.includes(x.fundCode)) {
                        const colStart = String.fromCharCode(65 + rwnI)
                        rowWithNames[colStart] = x.name
                        const startMerge = XLSX.utils.decode_cell(colStart + rowWithNamesNum)
                        rwnI++
                        const colEnd = String.fromCharCode(65 + rwnI)
                        rowWithNames[colEnd] = null
                        const endMerge = XLSX.utils.decode_cell(colEnd + rowWithNamesNum)
                        rwnI++
                        ws['!merges']?.push({s: startMerge, e: endMerge})
                        ws[colStart + rowWithNamesNum] = {}
                        ws[colStart + rowWithNamesNum].s = styleForNames
                    }
                })

                additionalRows.push(rowWithNames)
            } else {
                // XLSX - PROFIL - JEDEN FUNDUSZ
                headerRowForFundData = 4
                ws['!rows'] = [{hpx: 25}, {hpx: 20}, {hpx: 20}, {hpx: 30}]
            }
            XLSX.utils.sheet_add_json(ws, additionalRows, {skipHeader: true, origin: 0})
            XLSX.utils.sheet_add_json(ws, transposedData, {origin: additionalRows.length})

            ws['A2'].s = {
                font: {sz: 18, color: {rgb: '006192'}},
                // alignment: { indent: 1, horizontal: "left" }
            }

            ws['A3'].s = {
                font: {sz: 11, color: {rgb: '767676'}},
            }

            ws['!cols'] = []
            for (let col = 0; col < maxColNum; col++) {
                const cellName = String.fromCharCode(col + 65) + '' + headerRowForFundData
                ws[cellName].s = {
                    font: {sz: 10, bold: true},
                    alignment: {vertical: 'center'},
                    border: {
                        bottom: {style: 'thin', color: {rgb: '000000'}},
                    },
                }
                ws['!cols'].push({wch: 20})
            }

            //clear fund names from json transposedData
            let modifyValueCurrencyIter: number = 1
            let cellName: string
            //to override A5 style - defined in loop
            const valueCurrencyStyle = {
                font: {sz: 10, bold: true},
                alignment: {horizontal: 'center', vertical: 'center'},
                border: {
                    bottom: {style: 'thin', color: {rgb: '000000'}},
                    top: chartMode == 'compare' ? {style: 'thin', color: {rgb: '000000'}} : undefined,
                },
            }

            fundNames.forEach((fName) => {
                const fNameLen = fName.length
                cellName = String.fromCharCode(modifyValueCurrencyIter + 65) + '' + headerRowForFundData
                //start from B
                ws[cellName].v = ws[cellName]?.v.slice(0, -fNameLen)
                ws[cellName].s = valueCurrencyStyle
                modifyValueCurrencyIter++
                cellName = String.fromCharCode(modifyValueCurrencyIter + 65) + '' + headerRowForFundData
                ws[cellName].v = ws[cellName]?.v.slice(0, -fNameLen)
                ws[cellName].s = valueCurrencyStyle
                modifyValueCurrencyIter++
            })

            // ws['!freeze'] = {row: headerRowForFundData} // does not work?

            tableDataCentering(ws, headerRowForFundData, maxColNum)
            firstColIndent(ws)

            // white background and hidden borders for header
            // ws['!merges']?.push(XLSX.utils.decode_range('A1:F1'))
            if (chartMode == 'profile') {
                ws['!merges']?.push(XLSX.utils.decode_range('A2:F2'))
            } else if (chartMode === 'compare') {
                ws['!merges']?.push(XLSX.utils.decode_range('A2:C2'))
            }
            ws['!merges']?.push(XLSX.utils.decode_range('A3:C3'))
            // for (let row = 0; row <=additionalRows.length-1; row++) {
            //     for (let col = 0; col <= 5; col++) {
            //         const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            //         ws[cellAddress] = ws[cellAddress] || {};
            //         const currStyle = ws[cellAddress]?.s
            //         ws[cellAddress].s = {
            //             ...currStyle,
            //             ...{
            //                 border: {
            //                     top: { style: "thin", color: { rgb: "FFFFFF" } },
            //                     bottom: { style: "thin", color: { rgb: "FFFFFF" } },
            //                     left: { style: "thin", color: { rgb: "FFFFFF" } },
            //                     right: { style: "thin", color: { rgb: "FFFFFF" } }
            //                 },
            //             }
            //         };
            //     }
            // }
            setFontFamily(ws, 'Arial')
            const wb = XLSX.utils.book_new()
            if (globalCtx.layout == 'dfe') XLSX.utils.book_append_sheet(wb, ws, t('label.xlsx.dfe'))
            else if (chartMode === 'compare') XLSX.utils.book_append_sheet(wb, ws, t('label.xlsx.compare.filename'))
            else XLSX.utils.book_append_sheet(wb, ws, t('label.xlsx.profile.tab'))

            XLSX.writeFile(wb, filename + '.xlsx')
            // buildSpreadsheet()
        } else if (extension === 'pdf') {
            console.log('Pobieram pdf...')
            const fileDateFormatted = format(fileDate, 'dd.MM.yyyy') + ' r.'

            const compareClone = document.getElementById('compareClone')
            const legendClone = document.getElementById('legendClone')
            const pdfChart = document.getElementById('pdfChart')
            const pdfNote = document.getElementById('pdfNote')
            if (!compareClone || !legendClone || !pdfChart || !pdfNote) {
                console.warn('DOM incomplete for generating pdf')
                return
            }
            // clones clean
            compareClone.innerHTML = ''
            legendClone.innerHTML = ''
            pdfChart.innerHTML = ''

            // ### Pages content ###
            // #Part 1 - compareClone
            const headerCompareId = chartMode === 'compare' ? 'compare-container-toolbar' : 'container-toolbar'
            const headerCompare = document.getElementById(headerCompareId)?.cloneNode(true) as Element
            compareClone?.append(headerCompare)
            const tableCompare = document.querySelector('.fund-table .fund-table-all')?.cloneNode(true) as Element
            tableCompare?.querySelectorAll('.download').forEach((el) => el.remove())
            tableCompare?.querySelectorAll('.more').forEach((el) => el.remove())
            compareClone?.append(tableCompare)
            // #Part 2 - legendClone
            const legendHeader = document.getElementById('legendHeader')?.cloneNode(true) as Element
            pdfNote.dataset.title = disclaimers[pdfLayout].title[language]
            legendClone?.append(legendHeader)
            const legendTable = document.querySelector('#tableLegend .fund-table-all')?.cloneNode(true) as Element
            legendTable.querySelectorAll('.download').forEach((el) => el.remove())
            legendClone?.append(legendTable)
            // #Part 3 - pdfChart
            const chartHeader = document.querySelector('.aol-fund-profile-chart-header')?.cloneNode(true) as Element
            const legendDotsAfterChart = document.getElementById('legendDotsAfterChart')?.cloneNode(true) as Element
            legendDotsAfterChart?.querySelectorAll('.download-table-xlsx').forEach((el) => el.remove())
            drawChart(1118, 383, '#pdfChart', 'pdfSvg')
            pdfChart?.prepend(chartHeader)
            pdfChart?.append(legendDotsAfterChart)

            // page numeration and move content
            if (chartMode === 'profile') compareClone.classList.remove('pdfSection')
            else compareClone.classList.add('pdfSection')

            const itemsNumber: number = chartData.data.length

            // type yPosType = 'bottom'|'center'|`${number}`
            let maxPages = 2
            if (chartMode === 'profile') {
                legendClone.dataset.ypos = '50'
                pdfChart.dataset.ypos = '70'
                legendClone.dataset.newpage = 'false'
                pdfChart.dataset.newpage = 'false'
                pdfNote.dataset.newpage = 'true'
            } else if (itemsNumber < 6) {
                if (itemsNumber === 1) {
                    compareClone.dataset.ypos = '50'
                    legendClone.dataset.ypos = '70'
                } else if (itemsNumber > 1 && itemsNumber < 5) {
                    const pTop: number = (itemsNumber - 5) * -30
                    if (pTop > 0) {
                        compareClone.dataset.ypos = pTop.toString()
                        legendClone.dataset.ypos = pTop.toString()
                    }
                } else {
                    //5 funds
                    legendClone.dataset.ypos = 'bottom'
                }
                legendClone.dataset.newpage = 'false'
                pdfChart.dataset.newpage = 'true'
                pdfNote.dataset.newpage = 'false'
                pdfNote.dataset.ypos = '20'
            } else {
                compareClone.dataset.ypos = '30'
                legendClone.dataset.newpage = 'true'
                if (itemsNumber == 10 && isLegendAdditionalNote()) {
                    pdfChart.dataset.newpage = 'true'
                    pdfNote.dataset.ypos = '20'
                    pdfNote.dataset.newpage = 'false'
                } else {
                    pdfChart.dataset.newpage = 'false'
                    if (itemsNumber == 10 && chartItems.length > 3) pdfChart.dataset.ypos = '10'
                    else pdfChart.dataset.ypos = '20'
                    pdfNote.dataset.newpage = 'true'
                }
                maxPages = 3
            }
            // return

            const elements = document.querySelectorAll('.pdfSection') as NodeListOf<HTMLElement>
            const logoElement = document.getElementById('logoContainer') as HTMLElement

            let mainTitle: string = ''

            if (chartMode === 'profile') mainTitle = fundChartData[0].name
            else if (globalCtx.layout === 'dfe') mainTitle = 'Allianz Polska DFE'
            else if (globalCtx.layout === 'ofe') mainTitle = 'Allianz Polska OFE'
            else mainTitle = t('label.pdf.compare.title')

            let colorChangePos: number
            if (chartMode === 'compare') colorChangePos = mainTitle.indexOf(' ')
            else colorChangePos = mainTitle.lastIndexOf(' ')

            if (elements.length > 0) {
                const docWidth = 595
                const docHeight = 842
                const contentWidth = 523
                const marginTop = 40
                const marginBottom = 60
                // const usableHeight = docHeight - marginTop - marginBottom
                const marginLeft = (docWidth - contentWidth) / 2
                const primaryColor = '#003781'
                const secondaryColor = '#55b7d9'

                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'px',
                    format: [docWidth, docHeight],
                    compress: true,
                })
                readCustomFonts(pdf)
                setCustomFont(pdf, 'AllianzNeo-600')
                let pageNumber = 1
                let verticalOffset = marginTop
                let isNewPage = true

                const processLogo = logoElement
                    ? html2canvas(logoElement, {scale: 1, useCORS: true, logging: false}).then((canvas) => {
                          const maxLogoHeight = 30
                          const logoAspectRatio = canvas.width / canvas.height
                          const logoHeight = Math.min(maxLogoHeight, canvas.height / 2)
                          const logoWidth = logoHeight * logoAspectRatio
                          return {
                              image: canvas.toDataURL('image/png'),
                              width: logoWidth,
                              height: logoHeight,
                          }
                      })
                    : Promise.resolve(null)

                const canvasPromises = Array.from(elements).map((element) => {
                    const originalWidth = element.offsetWidth
                    const scaleFactor = contentWidth / originalWidth

                    return html2canvas(element, {scale: 1, useCORS: true, logging: false}).then((canvas) => {
                        const imgHeight = element.offsetHeight * scaleFactor
                        return {
                            canvas,
                            scaledHeight: imgHeight,
                            title: element.getAttribute('data-title'),
                            newPage: element.getAttribute('data-newpage') === 'true',
                            yPos: element.getAttribute('data-ypos') ?? '0',
                        }
                    })
                })

                const printFoot = (pdf: jsPDF) => {
                    setCustomFont(pdf, 'AllianzNeo-400')
                    pdf.setFontSize(10)
                    pdf.setTextColor('#414141')
                    pdf.text(`${t('label.pdf.common.page')} ${pageNumber}/${maxPages}`, docWidth - 40, docHeight - 50, {
                        align: 'right',
                    })
                    pdf.setTextColor('#767676')
                    pdf.text(`${t('label.pdf.common.timeline')} ${fileDateFormatted}`, docWidth - 40, docHeight - 35, {
                        align: 'right',
                    })
                    pageNumber++
                    isNewPage = false
                }

                Promise.all([processLogo, ...canvasPromises]).then(([logoData, ...results]) => {
                    // Dodajemy logo i tytuł na pierwszej stronie
                    if (logoData) {
                        pdf.addImage(logoData.image, 'PNG', marginLeft, verticalOffset, logoData.width, logoData.height)
                        verticalOffset += logoData.height + 20
                    }

                    if (mainTitle) {
                        const titleFontSize = 37
                        setCustomFont(pdf, 'AllianzNeo-400')
                        pdf.setFontSize(titleFontSize)
                        pdf.setTextColor(primaryColor)
                        // verticalOffset += 60 // odstęp pod tytułem głównym

                        //orphaned letter in mainTitle - move prev word to next line
                        const linesOfTitle = pdf.splitTextToSize(mainTitle, contentWidth)
                        if (
                            linesOfTitle.length > 1 &&
                            linesOfTitle[linesOfTitle.length - 1] &&
                            linesOfTitle[linesOfTitle.length - 1].length
                        ) {
                            mainTitle = mainTitle.replace(/^(.*) (\S+) (\S+)$/, '$1\n$2 $3')
                        }

                        const posAfterMultiline = addMultiColorText(
                            pdf,
                            mainTitle,
                            colorChangePos,
                            primaryColor,
                            secondaryColor,
                            {
                                startX: marginLeft,
                                startY: verticalOffset + 30,
                                lineHeight: titleFontSize,
                                maxWidth: contentWidth,
                            }
                        )
                        verticalOffset = posAfterMultiline.y + 20 // odstęp pod tytułem głównym
                        setCustomFont(pdf, 'AllianzNeo-600')
                    }

                    printFoot(pdf)

                    results.forEach((result, sectionIndex) => {
                        const {canvas, scaledHeight: imgHeight, title, newPage, yPos} = result

                        if (yPos) {
                            if (yPos === 'bottom') {
                                verticalOffset = docHeight - (imgHeight + marginBottom + marginTop)
                            } else if (yPos === 'center') {
                                const yLeft = docHeight - marginBottom - verticalOffset
                                const midLeft = (yLeft - imgHeight) / 2
                                verticalOffset += midLeft
                            } else if (yPos != '0') {
                                verticalOffset += parseInt(yPos)
                            }
                        }

                        let heightLeft = imgHeight
                        let position = 0
                        const titleSpacingAbove = 20
                        const titleHeight = title ? 10 : 0
                        const titleLineHeight = 6
                        const totalTitleBlockHeight = title ? titleSpacingAbove + titleHeight + titleLineHeight : 0

                        const needsNewPage =
                            newPage || verticalOffset + totalTitleBlockHeight > docHeight - marginBottom

                        if (needsNewPage) {
                            pdf.addPage()
                            verticalOffset = marginTop
                            isNewPage = true
                        }

                        if (title) {
                            if (!isNewPage) verticalOffset += titleSpacingAbove
                            setCustomFont(pdf, 'AllianzNeo-600')
                            pdf.setFontSize(16)
                            pdf.setTextColor(primaryColor)
                            pdf.text(title, marginLeft, verticalOffset)

                            const lineY = verticalOffset + 6
                            pdf.setDrawColor(primaryColor)
                            pdf.setLineWidth(1.2)
                            pdf.line(marginLeft, lineY, marginLeft + contentWidth, lineY)

                            verticalOffset += titleHeight + titleLineHeight
                        }

                        while (heightLeft > 0) {
                            const spaceLeftOnPage = docHeight - marginBottom - verticalOffset
                            const currentHeight = Math.min(heightLeft, spaceLeftOnPage)

                            if (currentHeight < 1) {
                                pdf.addPage()
                                verticalOffset = marginTop
                                isNewPage = true
                                continue
                            }

                            const tempCanvas = document.createElement('canvas')
                            tempCanvas.width = canvas.width
                            tempCanvas.height = (currentHeight / imgHeight) * canvas.height

                            const ctx = tempCanvas.getContext('2d')
                            ctx.drawImage(
                                canvas,
                                0,
                                (position / imgHeight) * canvas.height,
                                canvas.width,
                                tempCanvas.height,
                                0,
                                0,
                                tempCanvas.width,
                                tempCanvas.height
                            )

                            const tempImgData = tempCanvas.toDataURL('image/png')

                            pdf.addImage(tempImgData, 'PNG', marginLeft, verticalOffset, contentWidth, currentHeight)
                            verticalOffset += currentHeight
                            position += currentHeight
                            heightLeft -= currentHeight

                            if (isNewPage) {
                                printFoot(pdf)
                            }
                        }
                    })

                    const disclaimer = extractTextAndLinks(disclaimers[pdfLayout].content[language])

                    pdf.setTextColor('#414141')
                    setCustomFont(pdf, 'AllianzNeo-400')
                    //space after note title
                    verticalOffset += 10
                    const finalY = addLinkedText(pdf, disclaimer.content, marginLeft, verticalOffset, {
                        maxWidth: contentWidth,
                        lineHeight: 12,
                        paragraphSpacing: 10,
                        fontSize: 10.65,
                        links: disclaimer.links,
                    })
                    if (pdfNote.dataset.newpage === 'true') {
                        printFoot(pdf)
                    }
                    pdf.save(filename + '.pdf')
                    setIsPdfLoading(false)
                })
            }

            drawChart(
                chartRef.current.clientWidth,
                chartRef.current.clientHeight,
                '.aol-fund-profile-chart-content',
                'mainSvg'
            )
            // clone.remove()
            // chartContainer.classList.remove('hidden')
        } else if (extension === 'pdf-api') {
            const jsonData = prepareJsonData(filename)
            downloadPdf(jsonData)
                .then((result) => {
                    console.log('pdf')
                    // console.log(jsonData)
                })
                .catch((error) => {
                    console.error(error)
                })
        } else if (extension === 'json') {
            console.log('json')

            const jsonData = prepareJsonData(filename)

            downloadJson(jsonData)
                .then((result) => {
                    console.log('downloading json')
                    // console.log(jsonData)
                })
                .catch((error) => {
                    console.error(error)
                })
        }
    }

    //     const buildSpreadsheet = async()=>{
    //         const workbook = new ExcelJS.Workbook();
    //         const sheet = workbook.addWorksheet("Raport");
    //
    //         sheet.views = [{ showGridLines: false }];
    //
    //         sheet.mergeCells("A1:C1");
    //         sheet.getCell("A1").value = "Wyniki porównań funduszy";
    //         sheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    //         sheet.getCell("A1").font = { bold: true, size: 16, color: { argb: "FF0000" } };
    //
    //         const blob = await workbook.xlsx.writeBuffer();
    //         saveAs(new Blob([blob]), "Raport.xlsx");
    // }

    const [loading, setLoading] = useState(false)

    const [error, setError] = useState(null)

    const prepareJsonData = (filename: string) => {
        const legendChartData = fundChartData.map((fund) => {
            const {quotation} = fund

            const firstQuotation =
                quotation.find((q) => new Date(q.date).getTime() === startDateB.getTime()) || quotation[0]

            const showDateFirstQuotation = firstQuotation.date !== yyyyMMdd(startDateB)

            // Get the last quotation
            const lastQuotation =
                quotation.find((q) => new Date(q.date).getTime() === endDateB.getTime()) ||
                quotation[quotation.length - 1]
            const showDateLastQuotation = lastQuotation.date !== yyyyMMdd(endDateB)

            // Create a new quotation array with the desired structure
            const newQuotation = []
            let ror = 0
            if (firstQuotation && lastQuotation) {
                // @ts-ignore
                newQuotation.push({
                    value: firstQuotation.value,
                    date: firstQuotation.date,
                    showDate: showDateFirstQuotation,
                })

                // @ts-ignore
                newQuotation.push({
                    value: lastQuotation.value,
                    date: lastQuotation.date,
                    showDate: showDateLastQuotation,
                })
                ror = ((lastQuotation.value - firstQuotation.value) / firstQuotation.value) * 100
            }

            return {
                fundCode: fund.fundCode,
                name: fund.name,
                currency: fund.currency,
                color: getFundColor(fund.fundCode),
                toggled: chartItems.includes(fund.fundCode),
                quotation: newQuotation,
                ror: ror,
            }
        })

        const lastSpaceIndex = legendChartData[0].name.lastIndexOf(' ')

        return {
            layout: pdfLayout,
            language: language,
            filename: filename,
            headerPart1:
                chartMode === 'profile' || (chartMode === 'catalog' && pdfLayout === 'ofe')
                    ? legendChartData[0].name.substring(0, lastSpaceIndex)
                    : t('label.pdf.compare.header.p1'),
            headerPart2:
                chartMode === 'profile' || (chartMode === 'catalog' && pdfLayout === 'ofe')
                    ? legendChartData[0].name.substring(lastSpaceIndex + 1)
                    : t('label.pdf.compare.header.p2'),
            compareTitle:
                chartMode === 'catalog' && pdfLayout === 'ofe'
                    ? t('label.pdf.compare.profile.title')
                    : t('label.pdf.compare.compare.title'),
            compareTextDate: t('label.datepicker.title2'),
            compareTextCurrent: t('label.datepicker.title3'),
            compareTextNameColumn:
                chartMode === 'catalog' && pdfLayout === 'dfe' ? t('label.column.unitCategory') : null,
            compareTextValue: t('label.xlsx.common.value'),
            compareTextValueOfUnit: t('label.row.valuation'),
            compareTextExclusively: t('label.fund.fio.exclusively.allianz'),
            compareAlternative:
                chartMode === 'catalog' && pdfLayout === 'ofe'
                    ? t('label.pdf.compare.profile.alternative')
                    : t('label.pdf.compare.compare.alternative'),
            legendTextPeriod: t('label.chart.select.period'),
            legendTextDateStart: t('label.compare.datepicker.begin'),
            legendTextDateEnd: t('label.compare.datepicker.end'),
            legendTextFundColumn: t('label.compare.visible.fund'),
            legendTextValueColumn: t('label.row.valuation'),
            legendTextRorColumn: t('label.compare.rate-of-return'),
            legendTextFundStarted: t('label.chart.legend.fund-started'),
            legendTextFundEnded: t('label.chart.legend.fund-last-quotation'),
            legendAlternative: t('label.pdf.compare.legend.alternative'),
            chartTitle:
                chartMode === 'profile' || (chartMode === 'catalog' && pdfLayout === 'ofe')
                    ? t('label.pdf.profile.chart.title')
                    : t('label.pdf.compare.chart.title'),
            chartTextRor: t('label.chart.tab.rate-of-return'),
            chartTextValuation: t('label.chart.tab.valuation'),
            chartTextEuroInfo: t('label.chart.info.fund'),
            chartAlternative: t('label.pdf.compare.chart.alternative'),
            footerPage: t('label.pdf.common.page'),
            footerDownloaded: t('label.pdf.common.timeline'),
            disclaimerTitle: disclaimers[pdfLayout].title[language],
            disclaimerContent: disclaimers[pdfLayout].content[language],
            periodMap: invRor,
            activePeriod: activePeriod,
            chartMode: chartMode,
            activeChartTab: activeTab,
            fundComparedData: sortedComparedRows,
            fundComparedSelectedColumns:
                pdfLayout === 'fio' ? ['SRI', ...selectedRateOfReturnOptions] : selectedRateOfReturnOptions,
            fundComparedSortDir: chartMode === 'compare' ? ctx.sortCompareDir : ctx.sortDir,
            fundComparedSortCol: chartMode === 'compare' ? ctx.sortCompareCol : ctx.sortCol,
            fundChartData: fundChartData,
            date: ctx.date === null || chartMode === 'profile' ? null : format(ctx.date, 'dd.MM.yyyy'),
            dateStart: startDateB === null ? null : format(startDateB, 'dd.MM.yyyy'),
            dateEnd: endDateB === null ? null : format(endDateB, 'dd.MM.yyyy'),
            legendChartData: legendChartData,
            chartItems: chartItems,
        }
    }

    const downloadPdf = async (jsonData) => {
        setLoading(true)
        setError(null)

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/pdf`, jsonData, {
                headers: {
                    Accept: 'application/json',
                },
                auth: {
                    username: `${process.env.REACT_APP_API_USERNAME}`,
                    password: `${process.env.REACT_APP_API_PASSWORD}`,
                },
                responseType: 'blob', // Set response type to blob
            })

            // Create a Blob from the PDF Stream
            const blob = new Blob([response.data], {type: 'application/pdf'})

            // Create a link element
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.setAttribute('download', jsonData.filename + '.pdf') // Set the file name

            // Append to the body
            document.body.appendChild(link)

            // Trigger the download
            link.click()

            // Clean up and remove the link
            link.parentNode.removeChild(link)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const downloadJson = async (jsonData) => {
        setLoading(true)
        setError(null)

        try {
            const dataStr = JSON.stringify(jsonData, null, 2)

            const blob = new Blob([dataStr], {type: 'application/json'})

            const url = URL.createObjectURL(blob)

            const a = document.createElement('a')

            a.href = url

            a.download = jsonData.filename + '.json'

            document.body.appendChild(a)

            a.click()

            document.body.removeChild(a)

            URL.revokeObjectURL(url)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const periodSelectorClassName = (value: IPeriodValues): string => {
        const matchingPeriod = findPeriodKey(startDateB as Date, endDateB as Date)
        const key: string = value

        if (periods[key as keyof Periods] !== undefined) {
            return matchingPeriod === value ? 'border-period flex active' : 'border-period flex'
        } else {
            return 'border-period flex inactive'
        }
    }

    const toggleChartItems = useCallback(
        (id: string) => {
            const prevChartItems = chartItems
            let newItems: string | any[]
            const isCharItemVisible = chartItems.includes(id)

            if (isCharItemVisible) {
                newItems = prevChartItems.filter((item: string) => item !== id)
                setChartItems(newItems)
                setColors((prevColors) => {
                    const indexToRemove = chartItems.indexOf(id)
                    const newColors = [...prevColors]
                    newColors.splice(indexToRemove, 1)
                    return newColors
                })
            } else {
                if (chartItems.length < chartItemLimit) {
                    newItems = [...prevChartItems, id]
                    setChartItems(newItems)
                    const availableColor = defaultColors.find((color) => !colors.includes(color))
                    if (availableColor) {
                        setColors((prevColors) => {
                            return [...new Set([...prevColors, availableColor])] // Ensure unique colors
                        })
                    }
                }
            }

            const prevDInt = structuredClone(domainInterval)
            const prevStartDateB = structuredClone(startDateB)
            const prevEndDateB = structuredClone(endDateB)

            if (newItems.length === 0) {
                setDomainInterval({min: startDate, max: endDate})
                setPeriods(getPeriodsForDate(null))
                setStartDateB(startDate)
                setEndDateB(endDate)
            } else {
                const domain = getChartDomain(newItems)
                setDomainInterval(domain)
                setPeriods(getPeriodsForDate(domain))
                if (!(!isBefore(startDateB, domain.min) && !isAfter(endDateB, domain.max))) {
                    setIsAutoSwitchPeriodInfo(true)
                    setStartDateB(domain.min)
                    setEndDateB(domain.max)
                }
                if (isSameDay(prevDInt.min, prevStartDateB) && isSameDay(prevDInt.max, prevEndDateB)) {
                    setStartDateB(domain.min)
                    setEndDateB(domain.max)
                }
            }
        },
        [chartItems, colors, chartData, startDateB, endDateB]
    )

    const periodMap: IPeriodItem[] = [
        {label: '1D', value: 'one_day'},
        {label: '1M', value: 'one_month'},
        {label: '3M', value: 'three_months'},
        {label: '6M', value: 'six_months'},
        {label: '12M', value: 'one_year'},
        {label: '36M', value: 'three_years'},
        {label: '60M', value: 'five_years'},
        {label: 'YTD', value: 'year_to_date'},
        {label: 'MAX', value: 'max'},
    ]

    const atLeastOneWithForeignCurrency = (): boolean => {
        const regex = /^ALL103/g
        return chartItems.some((item: string) => regex.test(item))
    }

    const calcRateOfReturn = (fundItem: FundChartData) => {
        const startDateString = yyyyMMdd(startDateB)
        const endDateString = yyyyMMdd(endDateB)

        const startValue = getValueForDate(fundItem.quotation, startDateString, null)
        const endValue = getValueForDate(fundItem.quotation, endDateString, null)

        let percentageChange: any = 0
        if (startValue && endValue) {
            percentageChange = ((endValue - startValue) / startValue) * 100
        }

        return startValue && endValue ? (
            <span className="legend-fund-name">
                {fundItem.name}&nbsp;&nbsp;[
                {percentageChange.toLocaleString('pl-PL', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}
                %]
            </span>
        ) : (
            <span className="legend-fund-name">{fundItem.name}&nbsp;&nbsp;[&ndash;] </span>
        )
    }

    const isLegendAdditionalNote = useCallback(() => {
        return (
            chartMode === 'compare' &&
            globalCtx.layout === 'fio' &&
            atLeastOneWithForeignCurrency() === true &&
            activeTab === 'quotationTab'
        )
    }, [chartMode, globalCtx, atLeastOneWithForeignCurrency, activeTab])

    return (
        <>
            <section className="aol-fund-profile">
                <div className="veil" style={{width: `calc(100vw - var(--scrollbar-width))`}}></div>
                {(chartMode === 'compare' || chartMode === 'catalog') && (
                    <h2 className="title-chart flex ">{t('label.compare.title')}</h2>
                )}
                <div id={'legendHeader'}>
                    <div className="flex" style={{flexDirection: 'column'}}>
                        <div className="standardized-period flex">
                            <span className="flex">{t('label.chart.select.period')}</span>
                        </div>
                        <div className={'period-selector flex'}>
                            {periodMap.map((item: IPeriodItem, index: number): React.JSX.Element => {
                                return (
                                    <div
                                        key={index}
                                        data-begin={periods[item.value]?.begin}
                                        data-end={periods[item.value]?.end}
                                        className={periodSelectorClassName(item.value)}
                                        onClick={() => {
                                            handlePeriodClick(item.value)
                                        }}>
                                        <div className={'period flex'}>{item.label}</div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                    <div className="datepickers flex" style={{padding: '0'}}>
                        <div className="datepickers-item flex">
                            <label className="datepickers-item-label">{t('label.compare.datepicker.begin')}</label>
                            <YearMonthPicker
                                language={language}
                                selectedDate={startDateB}
                                setSelectedDate={setStartDateB}
                                datePickerMinDate={domainInterval.min as unknown as Date}
                                datePickerMaxDate={domainInterval.max as unknown as Date}
                                excludeDates={excludedDays}
                                filterDate={(date) => {
                                    return date.getDay() !== 0 && date.getDay() !== 6
                                }}
                            />
                        </div>

                        <div className="datepickers-item flex">
                            <label className="datepickers-item-label">{t('label.compare.datepicker.end')}</label>
                            <YearMonthPicker
                                language={language}
                                selectedDate={endDateB}
                                setSelectedDate={setEndDateB}
                                datePickerMinDate={domainInterval.min as unknown as Date}
                                datePickerMaxDate={domainInterval.max as unknown as Date}
                                filterDate={(date) => {
                                    return date.getDay() !== 0 && date.getDay() !== 6
                                }}
                                excludeDates={excludedDays}
                            />
                        </div>
                    </div>
                </div>
                {chartMode === 'compare' &&
                    standardizationInfo &&
                    sessionCtx.getItem('standardization') !== 'hidden' && (
                        <InfoBox
                            title={t('label.infobox.standardization.failed')}
                            onCloseCallback={() => {
                                sessionCtx.setItem('standardization', 'hidden')
                            }}
                        />
                    )}
                {chartMode === 'compare' && isAutoSwitchPeriodInfo && (
                    <InfoBox
                        title={t('label.infobox.periods.switch')}
                        onCloseCallback={() => {
                            setIsAutoSwitchPeriodInfo(false)
                        }}
                        sticky={true}
                    />
                )}
                <div className="aol-fund-profile-chart flex">
                    <div className="aol-fund-profile-chart-footer flex">
                        <LegendTable
                            fundChartData={fundChartData}
                            chartItems={chartItems}
                            startDateB={startDateB as Date}
                            endDateB={endDateB as Date}
                            toggleChartItems={toggleChartItems}
                            chartMode={chartMode}
                            chartItemLimit={chartItemLimit}
                            getFundColor={getFundColor}
                        />
                    </div>

                    <div className="aol-fund-profile-chart-header flex">
                        <ul className="aol-fund-profile-chart-nav flex">
                            <li
                                className={activeTab === 'rateOfReturnTab' ? 'active flex' : 'flex'}
                                onClick={handleRateOfReturnTab}>
                                {t('label.chart.tab.rate-of-return')}
                            </li>
                            <li
                                className={activeTab === 'quotationTab' ? 'active flex' : ' flex'}
                                onClick={handleQuotationTab}>
                                {t('label.chart.tab.valuation')}
                            </li>
                        </ul>
                    </div>

                    <div id="chartContainer">
                        <div
                            className="veil-chart"
                            style={{
                                width: `calc(100vw - var(--scrollbar-width))`,
                            }}>
                            {' '}
                        </div>
                        {activePeriod && chartItems.length > 0 ? (
                            <div className="chart-period ">
                                {t('label.chart.select.period')}:<span> {activePeriod}</span>
                            </div>
                        ) : (
                            ''
                        )}

                        <div className="aol-fund-profile-chart-content flex" ref={chartRef}></div>
                    </div>

                    {isPdfLoading && (
                        <ProgressBar
                            visible={true}
                            height="50"
                            width="80"
                            barColor="#009de6"
                            borderColor="#0d4ea6"
                            ariaLabel="progress-bar-loading"
                            wrapperStyle={{}}
                            wrapperClass="loaderProgressBar"
                        />
                    )}
                    <div id={'legendDotsAfterChart'} className="aol-fund-profile-chart-footer flex">
                        <div className="flex download-table-xlsx">
                            {chartItems.length >= 1 ? (
                                <button className="download-btn flex" onClick={() => handleDownloadBtn('xlsx')}>
                                    <span className="download-icon" style={{gap: '8px'}}>
                                        <span className="download-text download-text-xlsx">
                                            {t('label.btn.xlsx.download.fund')}
                                        </span>
                                        <SVGIcon
                                            id={'download-pdf'}
                                            className={'light-blue'}
                                            width={24}
                                            height={24}
                                            fill={'#006192'}
                                        />
                                    </span>
                                </button>
                            ) : (
                                ''
                            )}
                        </div>

                        {isLegendAdditionalNote() ? (
                            <div className="aol-fund-info flex">
                                <div className="legend flex" style={{paddingBottom: '32px'}}>
                                    <div className="legend-description flex">
                                        <span>
                                            <InfoIcon />
                                        </span>
                                        <span className="legend-fund-name">{t('label.chart.info.fund')}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            ''
                        )}
                        {(chartMode === 'compare' || (chartMode === 'catalog' && globalCtx.layout !== 'ofe')) && (
                            <div className="legend flex">
                                {fundChartData
                                    .filter((fundItem) => chartItems.includes(fundItem.fundCode))
                                    .map((fundItem) => (
                                        <div key={fundItem.fundCode} className="legend-description flex">
                                            <span
                                                // Add a unique key for each element
                                                className="chart-dot chart-dot-legend"
                                                style={{background: getFundColor(fundItem.fundCode)}} // Assuming fundItem has a color property
                                            ></span>
                                            {calcRateOfReturn(fundItem)}
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>

                    <div className={`aol-fund-profile-chart-download flex${isPdfLoading ? ' downloadInProgress' : ''}`}>
                        <div style={{display: 'none'}}>
                        {/*<div>*/}
                            <button onClick={() => handleDownloadBtn('json')} disabled={loading}>
                                {loading ? 'Downloading...' : 'Download json'}
                            </button>
                            <button onClick={() => handleDownloadBtn('pdf-api')} disabled={loading}>
                                {loading ? 'Downloading...' : 'Download pdf-api'}
                            </button>

                            {error && <p>Error: {error}</p>}
                        </div>
                        {chartItems.length >= 1 ? (
                            <button className="download-btn flex" onClick={() => setIsPdfLoading(true)}>
                                {/*<button className="download-btn flex" onClick={() => handleDownloadBtn('pdf-api')}>*/}
                                <span className="download-icon">
                                    <SVGIcon
                                        id={'icon-pdf'}
                                        className={'light-blue'}
                                        width={24}
                                        height={24}
                                        fill={'#006192'}
                                    />
                                    {chartMode === 'compare' || globalCtx.layout === 'dfe' ? (
                                        <span className="download-text download-text-rwd">
                                            {t('label.btn.pdf.download.compare')}
                                        </span>
                                    ) : (
                                        ''
                                    )}
                                    {chartMode === 'profile' ? (
                                        <span className="download-text">{t('label.btn.pdf.download')}</span>
                                    ) : (
                                        ''
                                    )}
                                    {globalCtx.layout === 'ofe' ? (
                                        <span className="download-text">{t('label.btn.pdf.download.fund')}</span>
                                    ) : (
                                        ''
                                    )}
                                </span>
                            </button>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
            </section>
            <div style={{position: 'absolute', opacity: 0, height: 0, overflow: 'hidden'}}>
                <div
                    id="pdfContainer"
                    className={'pdf-containers'}
                    style={{
                        width: '1118px',
                        position: 'relative',
                        border: '1px solid green',
                    }}>
                    <div id={'logoContainer'}>
                        <AllianzLogo />
                    </div>
                    <div
                        id="compareClone"
                        className={'pdfSection fund-table'}
                        data-title={
                            globalCtx.layout === 'ofe'
                                ? t('label.pdf.ofe.performance')
                                : t('label.pdf.compare.compare.title')
                        }></div>
                    <div
                        id="legendClone"
                        className={'pdfSection fund-table'}
                        data-title={t(
                            chartMode === 'profile' || globalCtx.layout === 'ofe'
                                ? t('label.pdf.profile.chart.title')
                                : t('label.pdf.compare.chart.title')
                        )}></div>
                    <div className="aol-fund-profile">
                        <div id="pdfChart" className={'pdfSection aol-fund-profile-chart'}></div>
                    </div>
                    <div className="pdfSection" id="pdfNote"></div>
                    <div style={{position: 'relative', backgroundColor: 'red', height: '1200px', width: '20px'}}></div>
                </div>
            </div>
        </>
    )
}

export default LineChart
